import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/PageHeader';
import { brandsApi } from '../api';
import { Loader2, Plus, Edit3, Trash2, Check, X } from 'lucide-react';

/**
 * Brand Master — SA/Admin-managed list of brand names.
 *
 * Each brand can be assigned to EBOs (branch scoping) and to SKUs
 * (catalog partitioning). A brand cannot be deleted while any EBO or
 * SKU still references it — the server enforces that with a 409.
 *
 * Simple two-column layout: list on the left, inline add row on top.
 * Each row supports rename + active/inactive toggle + delete.
 */
export default function BrandMaster() {
  const { user, theme } = useAuth();
  const canEdit = user?.role === 'SuperAdmin' || user?.role === 'Admin';

  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState('');
  const [editing, setEditing] = useState(null);           // brand name being edited
  const [editDraft, setEditDraft] = useState({ name: '', status: 'Active' });

  const refresh = async () => {
    setLoading(true); setErr('');
    try {
      const data = await brandsApi.list();
      setBrands(data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not load brands');
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const create = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true); setErr('');
    try {
      await brandsApi.create({ name, status: 'Active' });
      setNewName('');
      await refresh();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Create failed');
    } finally { setBusy(false); }
  };

  const startEdit = (b) => {
    setEditing(b.name);
    setEditDraft({ name: b.name, status: b.status || 'Active' });
  };

  const cancelEdit = () => { setEditing(null); setEditDraft({ name: '', status: 'Active' }); };

  const saveEdit = async () => {
    if (!editing) return;
    setBusy(true); setErr('');
    try {
      await brandsApi.update(editing, editDraft);
      cancelEdit();
      await refresh();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Update failed');
    } finally { setBusy(false); }
  };

  const remove = async (name) => {
    if (!window.confirm(`Delete brand "${name}"? Refuses if any EBO or SKU still references it.`)) return;
    setBusy(true); setErr('');
    try {
      await brandsApi.remove(name);
      await refresh();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Delete failed');
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="brand-master-page">
      <PageHeader
        title="Brand master"
        subtitle={
          <span>
            <span className="font-semibold text-[#232323]">{brands.length}</span> brands · used to scope EBOs and SKUs to a specific brand (e.g., an EBO tagged <span className="font-mono">FOREVER52</span> only sees FOREVER52 items when raising a requisition).
          </span>
        }
      />

      {canEdit && (
        <div className="fm-card p-4 mb-5">
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label className="block text-[12.5px] font-medium mb-1">New brand name</label>
              <input
                data-testid="brand-new-input"
                type="text"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && create()}
                placeholder="e.g. FOREVER52"
                className="fm-focus w-full border border-[#E7E2D3] rounded-md px-3 py-2 text-[13.5px] bg-white"
                maxLength={60}
              />
            </div>
            <button
              data-testid="brand-add-btn"
              onClick={create}
              disabled={busy || !newName.trim()}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-white text-[13.5px] font-medium disabled:opacity-40"
              style={{ background: theme.primary }}
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
              Add brand
            </button>
          </div>
        </div>
      )}

      <div className="fm-card overflow-hidden">
        <table className="w-full text-[13.5px]">
          <thead>
            <tr className="text-left text-[12px] text-[#7a6f5c] border-b border-[#E7E2D3]">
              <th className="px-5 py-3 font-medium">Brand name</th>
              <th className="px-5 py-3 font-medium">Status</th>
              <th className="px-5 py-3 font-medium">Created by</th>
              <th className="px-5 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={4} className="px-5 py-10 text-center text-[#7a6f5c]"><Loader2 className="inline animate-spin" size={18} /></td></tr>
            )}
            {!loading && brands.length === 0 && (
              <tr><td colSpan={4} className="px-5 py-10 text-center text-[#7a6f5c]">No brands yet — add your first above.</td></tr>
            )}
            {!loading && brands.map(b => {
              const isEditing = editing === b.name;
              return (
                <tr key={b.name} className="border-t border-[#EFEADC]" data-testid={`brand-row-${b.name}`}>
                  <td className="px-5 py-3 font-mono">
                    {isEditing ? (
                      <input
                        data-testid={`brand-edit-name-${b.name}`}
                        type="text"
                        value={editDraft.name}
                        onChange={e => setEditDraft(d => ({ ...d, name: e.target.value }))}
                        className="fm-focus w-full border border-[#E7E2D3] rounded-md px-2 py-1 text-[13.5px] bg-white"
                      />
                    ) : b.name}
                  </td>
                  <td className="px-5 py-3">
                    {isEditing ? (
                      <select
                        data-testid={`brand-edit-status-${b.name}`}
                        value={editDraft.status}
                        onChange={e => setEditDraft(d => ({ ...d, status: e.target.value }))}
                        className="border border-[#E7E2D3] rounded-md px-2 py-1 text-[13.5px] bg-white"
                      >
                        <option>Active</option>
                        <option>Inactive</option>
                      </select>
                    ) : (
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[11.5px] ${b.status === 'Inactive' ? 'bg-[#F5EEEE] text-[#B84A4A]' : 'bg-[#E9F1EA] text-[#2f6b41]'}`}>
                        {b.status || 'Active'}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-[#7a6f5c] font-mono text-[12.5px]">{b.createdBy || '—'}</td>
                  <td className="px-5 py-3 text-right">
                    {canEdit && !isEditing && (
                      <>
                        <button
                          data-testid={`brand-edit-btn-${b.name}`}
                          onClick={() => startEdit(b)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[12.5px] text-[#7a6f5c] hover:bg-[#F7F4EA] mr-1"
                        >
                          <Edit3 size={12} /> Edit
                        </button>
                        <button
                          data-testid={`brand-delete-btn-${b.name}`}
                          onClick={() => remove(b.name)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[12.5px] hover:bg-[#F7F4EA]"
                          style={{ color: '#B84A4A' }}
                        >
                          <Trash2 size={12} /> Delete
                        </button>
                      </>
                    )}
                    {canEdit && isEditing && (
                      <>
                        <button
                          data-testid={`brand-save-btn-${b.name}`}
                          onClick={saveEdit}
                          disabled={busy}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[12.5px] text-white mr-1 disabled:opacity-40"
                          style={{ background: theme.primary }}
                        >
                          {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Save
                        </button>
                        <button
                          onClick={cancelEdit}
                          disabled={busy}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[12.5px] text-[#7a6f5c] hover:bg-[#F7F4EA]"
                        >
                          <X size={12} /> Cancel
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {err && <div data-testid="brand-error" className="mt-4 text-[13px]" style={{ color: '#B84A4A' }}>{err}</div>}
    </div>
  );
}
